import { FaAngleLeft } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getSyncUserActivities } from "../../api/mainApi";

function MoodMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [fireflies, setFireflies] = useState([]);

  /* ---------- 0. Підвантаження активностей з сервера ------------------- */
  useEffect(() => {
    const fetchActivities = async () => {
      try {
        if (localStorage.getItem("moodActivitiesChanged") === "true") return;

        const data = await getSyncUserActivities();

        if (
          data &&
          Array.isArray(data.activities) &&
          data.activities.length > 0
        ) {
          localStorage.setItem(
            "moodActivities",
            JSON.stringify(data.activities),
          );
        }
      } catch (error) {
        console.error("Error fetching user activities:", error);
      }
    };

    fetchActivities();
  }, []);

  /* ---------- 1. Дані --------------------------------------------------- */
  const baseMoodImages = [
    {
      src: "/5.webp",
      mood: 5,
      additionalMood: null,
      colSpan: "col-span-1",
      tKey: "moods.5",
    },
    {
      src: "/4.webp",
      mood: 4,
      additionalMood: null,
      colSpan: "col-span-1",
      tKey: "moods.4",
    },
    {
      src: "/3.webp",
      mood: 3,
      additionalMood: null,
      colSpan: "col-span-2",
      tKey: "moods.3",
    },
    {
      src: "/2.webp",
      mood: 2,
      additionalMood: null,
      colSpan: "col-span-1",
      tKey: "moods.2",
    },
    {
      src: "/1.webp",
      mood: 1,
      additionalMood: null,
      colSpan: "col-span-1",
      tKey: "moods.1",
    },
  ];

  const premiumMoodImages = [
    ...baseMoodImages,
    { src: "/5.1.webp", mood: 5, additionalMood: 1, tKey: "moods.51" },
    { src: "/5.2.webp", mood: 5, additionalMood: 2, tKey: "moods.52" },
    { src: "/4.1.webp", mood: 4, additionalMood: 1, tKey: "moods.41" },
    { src: "/4.2.webp", mood: 4, additionalMood: 2, tKey: "moods.42" },
    { src: "/3.1.webp", mood: 3, additionalMood: 1, tKey: "moods.31" },
    { src: "/3.2.webp", mood: 3, additionalMood: 2, tKey: "moods.32" },
    { src: "/2.1.webp", mood: 2, additionalMood: 1, tKey: "moods.21" },
    { src: "/2.2.webp", mood: 2, additionalMood: 2, tKey: "moods.22" },
    { src: "/1.1.webp", mood: 1, additionalMood: 1, tKey: "moods.11" },
    { src: "/1.2.webp", mood: 1, additionalMood: 2, tKey: "moods.12" },
  ];

  /* преміум-статус */
  const premiumRaw = localStorage.getItem("premium");
  const isPremium =
    premiumRaw === "true" || (!isNaN(premiumRaw) && Number(premiumRaw) > 0);
  const images = isPremium ? premiumMoodImages : baseMoodImages;

  /* ---------- 2. Стан ---------------------------------------------------- */
  const [selectedMood, setSelectedMood] = useState(null);
  const [selectedAdditionalMood, setSelectedAdditionalMood] = useState(null);

  useEffect(() => {
    const m = localStorage.getItem("selectedMood");
    const a = localStorage.getItem("selectedAdditionalMood");
    if (m) setSelectedMood(Number(m));
    if (a) setSelectedAdditionalMood(Number(a));
  }, []);

  useEffect(() => {
    if (selectedMood !== null) return;

    const generateFirefly = () => ({
      id: Math.random(),
      x: 10 + Math.random() * 80,
      y: 10 + Math.random() * 60,
      delay: Math.random() * 2,
      duration: 2 + Math.random() * 2,
    });

    setFireflies(Array.from({ length: 48 }, generateFirefly));

    const interval = setInterval(() => {
      setFireflies((prev) => {
        const newFireflies = [...prev];
        const indexToReplace = Math.floor(Math.random() * newFireflies.length);
        newFireflies[indexToReplace] = generateFirefly();
        return newFireflies;
      });
    }, 1500);

    return () => clearInterval(interval);
  }, [selectedMood]);

  const handleImgClick = (img) => {
    setSelectedMood(img.mood);
    setSelectedAdditionalMood(img.additionalMood);

    localStorage.setItem("selectedMood", String(img.mood));
    if (img.additionalMood !== null) {
      localStorage.setItem(
        "selectedAdditionalMood",
        String(img.additionalMood),
      );
    } else {
      localStorage.removeItem("selectedAdditionalMood");
    }
  };

  /* ---------- 3. Групування (для преміум) ------------------------------- */
  const groupedPrem = useMemo(() => {
    if (!isPremium) return [];
    const map = {};
    premiumMoodImages.forEach((img) => {
      (map[img.mood] = map[img.mood] || []).push(img);
    });
    return Object.values(map)
      .sort((a, b) => b[0].mood - a[0].mood)
      .map((row) =>
        row.sort((a, b) => (a.additionalMood ?? 0) - (b.additionalMood ?? 0)),
      );
  }, [isPremium]);

  /* ---------- 4. Підпис -------------------------------------------------- */
  const labelKey = images.find(
    (i) =>
      i.mood === selectedMood &&
      (i.additionalMood ?? null) === (selectedAdditionalMood ?? null),
  )?.tKey;

  // /* ---------- 5. Навігація ---------------------------------------------- */
  // const backButton = () => {
  //   localStorage.removeItem("selectedMood");
  //   localStorage.removeItem("selectedAdditionalMood");
  //   localStorage.removeItem("moodNote");
  //   localStorage.removeItem("selectedActivities");
  //   navigate("/");
  // };

  const isNextDisabled = selectedMood === null;

  /* ---------- 6. Рендер -------------------------------------------------- */
  return (
    <div className="flex flex-col h-screen overflow-hidden bg-[#061014] relative select-none font-sans text-white -m-4 p-4">
      {/* Фон */}
      <div
        className="absolute inset-0 pointer-events-none z-0"
        style={{
          background:
            "radial-gradient(circle at 50% 100%, oklch(30.2% 0.056 229.695) 0%, #061014 60%)",
        }}
      />
      <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] pointer-events-none z-0"></div>

      {/* Header */}
      <div className="relative z-50 w-full pt-6 pb-2 px-20">
        <p className="text-2xl font-semibold text-center text-white truncate w-full">
          {t("moods.selectTitle")}
        </p>
      </div>

      {/* Mood picker */}
      {!isPremium ? (
        /* --- базова версія: сітка --------------------------------------- */
        <div className="relative z-10 flex-1 flex justify-center items-center">
          <div className="flex-1 grid grid-rows-3 grid-cols-2">
            {baseMoodImages.map((img) => (
              <div
                key={img.src}
                className={`${img.colSpan} cursor-pointer`}
                onClick={() => handleImgClick(img)}
              >
                <img
                  src={img.src}
                  alt={t(img.tKey)}
                  className="mx-auto w-32 aspect-square rounded-full p-3 pb-2 relative overflow-hidden transition-transform active:scale-95"
                  style={{
                    background:
                      selectedMood === img.mood &&
                      (selectedAdditionalMood ?? null) === null
                        ? "radial-gradient(circle, rgba(52,211,153,0.3) 0%, rgba(52,211,153,0) 70%)"
                        : "transparent",
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      ) : (
        /* --- преміум: рядки -------------------------------------------- */
        <div className="relative z-10 flex-1 flex flex-col justify-center items-center gap-2 py-4 px-2 overflow-hidden">
          {groupedPrem.map((row) => (
            <div
              key={row[0].src}
              className="flex justify-center gap-2 w-full"
              style={{ flex: "1 1 0", minHeight: 0 }}
            >
              {row.map((img) => (
                <div
                  key={img.src}
                  onClick={() => handleImgClick(img)}
                  className="cursor-pointer flex-1 flex items-center justify-center transition-transform active:scale-95"
                  style={{ maxWidth: "33.333%" }}
                >
                  <img
                    src={img.src}
                    alt={t(img.tKey)}
                    className="w-full h-full object-contain p-2 relative rounded-full"
                    style={{
                      maxHeight: "100%",
                      background:
                        selectedMood === img.mood &&
                        (selectedAdditionalMood ?? null) ===
                          (img.additionalMood ?? null)
                          ? "radial-gradient(circle, rgba(52,211,153,0.3) 0%, rgba(52,211,153,0) 60%)"
                          : "transparent",
                    }}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Footer Area */}
      <div className="relative z-20 mt-auto flex flex-col justify-between">
        {/* Label */}
        <div className="my-4 h-14 flex items-center justify-center pointer-events-none">
          {labelKey ? (
            <p className="text-4xl font-bold animate-fade-in drop-shadow-[0_2px_10px_rgba(0,0,0,0.8)]">
              {t(labelKey)}
            </p>
          ) : (
            <div className="relative w-full h-full flex items-center justify-center overflow-hidden">
              {fireflies.map((firefly) => (
                <div
                  key={firefly.id}
                  className="absolute"
                  style={{
                    left: `${firefly.x}%`,
                    top: `${firefly.y}%`,
                    animation: `glow ${firefly.duration}s ease-in-out ${firefly.delay}s infinite both`,
                  }}
                >
                  <svg width="8" height="8" viewBox="0 0 12 12">
                    <circle
                      cx="6"
                      cy="6"
                      r="2"
                      fill="oklch(95.6% 0.045 203.388)"
                      opacity="0.8"
                    />
                    <circle
                      cx="6"
                      cy="6"
                      r="4"
                      fill="oklch(95.6% 0.045 203.388)"
                      opacity="0.3"
                    />
                    <circle
                      cx="6"
                      cy="6"
                      r="6"
                      fill="oklch(95.6% 0.045 203.388)"
                      opacity="0.1"
                    />
                  </svg>
                </div>
              ))}
            </div>
          )}
        </div>

        <style>{`
          @keyframes glow {
            0%, 100% { opacity: 0; transform: scale(0.8); }
            50% { opacity: 1; transform: scale(1.2); }
          }
          @keyframes fade-in {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fade-in { animation: fade-in 0.4s ease-out; }
        `}</style>

        {/* Next Button */}
        <div className="pt-2 pb-2">
          <button
            onClick={() => navigate("/mood-note")}
            disabled={isNextDisabled}
            className={`block text-center text-3xl font-bold rounded-lg py-3 w-full shadow-lg transition-all duration-300 ${
              isNextDisabled
                ? "bg-white/5 border border-white/10 text-gray-500 cursor-not-allowed"
                : "bg-gradient-to-tr from-emerald-500 via-cyan-500 to-emerald-500 bg-gradient-size animate-gradient-move text-white shadow-[0_0_20px_rgba(16,185,129,0.3)]"
            }`}
          >
            {t("moods.continue")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default MoodMenu;
